// Lazily-loaded OU/account tree with checkboxes, for the StackSets target picker.
//
// Same rows, icons and chips as OrgTreeCheckbox. OUs and accounts are both
// selectable here (suspended accounts are not — AWS will not deploy to them),
// and one difference drives everything else: expansion is CONTROLLED by the page's
// useLazyOrgTree cache instead of local state, because a level does not exist
// until it is fetched. Every ROOT/OU row therefore gets a chevron
// unconditionally — asking whether a node has children in advance would cost the
// extra round trip per child that made the whole-tree load time out.
//
// OrgTreeCheckbox is left alone: the admin Policies picker is flat and needs a
// materialised tree, so one component cannot serve both without carrying two
// behaviours.
import { Box, Button, Checkbox, Chip, CircularProgress, Collapse, IconButton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/FolderOpen';
import CloudIcon from '@mui/icons-material/Cloud';
import { CK, FONT_BODY, statusChipSx } from '../../pages/portal/portalStyles';

const treeChipSx = (intent) => ({
  ...statusChipSx(intent),
  ml: 0.5,
  height: 20,
  fontSize: '0.65rem',
  flexShrink: 0,
});

const LazyOrgTreeCheckbox = ({ node, depth = 0, tree, selectedIds = [], onToggleSelect }) => {
  // The API returns both 'OU' and 'ORGANIZATIONAL_UNIT' for the same kind of node.
  const kind = node.type === 'ORGANIZATIONAL_UNIT' ? 'OU' : node.type;
  const canExpand = kind === 'ROOT' || kind === 'OU';
  const expanded = canExpand && Boolean(tree.expanded[node.id]);
  const children = tree.children[node.id];
  const loading = Boolean(tree.loading[node.id]);
  const error = tree.errors[node.id];
  const isSelected = selectedIds.some((n) => n.id === node.id);

  const getIcon = () => {
    switch (kind) {
      case 'ROOT': return <BusinessIcon sx={{ fontSize: '1rem', color: CK.blue, flexShrink: 0 }} />;
      case 'OU': return <FolderIcon sx={{ fontSize: '1rem', color: CK.amber, flexShrink: 0 }} />;
      case 'ACCOUNT': return <CloudIcon sx={{ fontSize: '1rem', color: CK.green, flexShrink: 0 }} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ ml: depth > 0 ? 3 : 0 }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', py: 0.5, px: 0.5, minWidth: 0,
          borderRadius: '6px', transition: 'background-color 0.2s ease',
          '&:hover': { backgroundColor: CK.hover },
        }}
      >
        {canExpand ? (
          <IconButton
            size="small"
            onClick={() => tree.toggle(node)}
            // Named per row: a tree of OUs whose every chevron announces only
            // "Expand" tells a screen-reader user nothing. Matches the
            // convention ResourceTree already uses in portalStyles.js.
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
            sx={{ mr: 0.5, p: 0.25, flexShrink: 0 }}
          >
            {loading ? (
              <CircularProgress size={14} sx={{ color: CK.amber }} />
            ) : (
              <ExpandMoreIcon
                sx={{
                  fontSize: '1rem',
                  transform: expanded ? 'none' : 'rotate(-90deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24, mr: 0.5, flexShrink: 0 }} />
        )}
        <Checkbox
          size="small"
          checked={isSelected}
          // Accounts are targetable in their own right; suspended ones are not —
          // AWS will not deploy to them and the count skips them, so offering the
          // checkbox would promise something that cannot happen.
          disabled={kind === 'ACCOUNT' && Boolean(node.status) && node.status !== 'ACTIVE'}
          // The RAW node.type, not the normalised `kind`: the page routes the
          // selection into targetOus vs targetAccounts and needs to tell an
          // unrecognised kind from a real one, which collapsing OU spellings here
          // would hide. Consumers must match kinds explicitly and ignore what they
          // do not recognise — never route the leftovers into OUs by default.
          onChange={() => onToggleSelect({ id: node.id, name: node.name, type: node.type })}
          inputProps={{ 'aria-label': node.name }}
          sx={{ p: 0.25, mr: 0.5, flexShrink: 0 }}
        />
        {getIcon()}
        <Typography
          noWrap
          sx={{
            ml: 0.5, fontFamily: FONT_BODY, fontSize: '0.85rem', color: CK.navy,
            minWidth: 0, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {node.name}
        </Typography>
        {kind === 'ACCOUNT' && node.id && (
          <Typography
            noWrap
            sx={{ ml: 0.5, fontFamily: 'monospace', fontSize: '0.75rem', color: CK.muted, flexShrink: 0 }}
          >
            {node.id}
          </Typography>
        )}
        <Chip
          label={kind}
          size="small"
          sx={treeChipSx(kind === 'ACCOUNT' ? 'success' : kind === 'OU' ? 'warning' : 'info')}
        />
        {node.status && node.status !== 'ACTIVE' && (
          <Chip label={node.status} size="small" sx={treeChipSx('error')} />
        )}
      </Box>

      {canExpand && (
        <Collapse in={expanded}>
          {error ? (
            <Box sx={{ ml: 3, py: 0.75, px: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.red }}>
                {error}
              </Typography>
              <Button
                size="small"
                onClick={() => tree.retry(node.id)}
                sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, p: 0.25 }}
              >
                Try again
              </Button>
            </Box>
          ) : !children ? (
            <Box sx={{ ml: 3, py: 0.75, px: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={12} sx={{ color: CK.amber }} />
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted }}>
                Loading…
              </Typography>
            </Box>
          ) : children.length === 0 ? (
            <Typography sx={{ ml: 3, py: 0.75, px: 0.5, fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted }}>
              This organizational unit is empty.
            </Typography>
          ) : (
            children.map((child) => (
              <LazyOrgTreeCheckbox
                key={child.id}
                node={child}
                depth={depth + 1}
                tree={tree}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
              />
            ))
          )}
        </Collapse>
      )}
    </Box>
  );
};

export default LazyOrgTreeCheckbox;
